import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Company, LiveEvent, User } from '@vd/types';
import { api, type AppFeatures, type ExtensionVersionState } from '../../lib/api';
import { whoIsSignedIn, type ApiError } from '../../lib/apiClient';
import { relayTicketChangeToExtension, tellExtensionWorkspaceChanged } from '../../lib/extensionPinRelay';
import { leavePage } from '../../lib/leavePage';
import { browserTimeZone, canonicalTimeZone, setDisplayTimeZone } from '../../lib/time';
import { holdPageAccount } from '../../lib/pageAccount';
import { purgeRecorderDataForSignOut } from '../recorder/recorderDb';
import { cancelRecorderSignOut, prepareRecorderForSignOut } from '../recorder/recorderSignOut';
import {
  authSessionEpoch,
  invalidateAuthSession,
  setAuthSessionUser,
} from '../../lib/authSessionEpoch';

interface AuthState {
  user: User | null;
  companies: Company[];
  loading: boolean;
  currentCompanyId: string | null;
  extensionVersion: ExtensionVersionState;
  features: AppFeatures;
  setCurrentCompany: (companyId: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-pull /me and replace the in-context user/companies. Call after
   *  any mutation that could change the signed-in user's identity
   *  (profile update, email change, company membership change) — the
   *  context is the source of truth for the shell chrome, so stale
   *  values show up until a full page reload otherwise. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const CURRENT_CO_KEY = 'vd-current-company';
const EMPTY_EXTENSION_VERSION: ExtensionVersionState = { version: null, status: null, managedHint: false };
const EMPTY_FEATURES: AppFeatures = { v2Recorder: false, v2RecorderRoutes: false };

function normalizeFeatures(features: Partial<AppFeatures> | undefined): AppFeatures {
  const v2Recorder = features?.v2Recorder === true;
  // An API from before the split sends only v2Recorder, which then meant both.
  return { v2Recorder, v2RecorderRoutes: features?.v2RecorderRoutes === true || v2Recorder };
}

/**
 * Purge every per-user `vd-*` key from localStorage on signOut. Previously
 * only `vd-current-company` was cleared, so on a shared machine the next
 * user saw the previous user's saved annotations, uploaded screenshot
 * data-URLs, and project MRU. This walks every key with the `vd-` prefix —
 * a coarse hammer, but none of our vd-prefixed keys are per-device settings
 * we'd want to keep across logouts.
 */
function purgeUserLocalState(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('vd-')) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
  } catch {
    // Safari private mode / quota — localStorage may throw; the session
    // cookie is already dead at this point so the user is effectively
    // logged out regardless.
  }
}

/**
 * The signed-in account, and the boundary around it: a page holds one account,
 * and when its session ends (sign-out, a 401) or another account arrives, the
 * page is left for a fresh load (`leavePage`), so nothing the ended session
 * had going, its cache included, outlives it into the next person's.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [extensionVersion, setExtensionVersion] = useState<ExtensionVersionState>(EMPTY_EXTENSION_VERSION);
  const [features, setFeatures] = useState<AppFeatures>(EMPTY_FEATURES);
  const [loading, setLoading] = useState(true);
  const [currentCompanyId, setCurrentCompanyIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(CURRENT_CO_KEY); } catch { return null; }
  });
  const userRef = useRef<User | null>(user);
  userRef.current = user;

  /**
   * Whether `nextUser` was let into this page. Another account, which /me
   * reports once somebody else has signed in from another tab, is not: it is
   * let in by a fresh load rather than into the page this one filled. The
   * account let in is the one every request from this page names
   * (`pageAccount.ts`), which is what stops another tab's sign-in from
   * answering for it.
   */
  const commitUser = useCallback((nextUser: User | null): boolean => {
    const current = userRef.current;
    if (current && nextUser && current.id !== nextUser.id) {
      leavePage();
      return false;
    }
    if (nextUser) holdPageAccount(nextUser.id);
    setAuthSessionUser(nextUser?.id ?? null);
    // Every time on the page is shown in this person's zone (TOADO-904).
    setDisplayTimeZone(nextUser?.timeZone);
    setUser(nextUser);
    return true;
  }, []);

  // A zone that follows the browser is saved from it on load, so emails and
  // the digest know it too (TOADO-904); one set by hand is left alone, so
  // travel does not change it.
  //
  // Only once /me has said how the zone was set: a sign-in commits a user
  // without it first, which read as "follows the browser" and wrote over a
  // zone chosen by hand (Codex, round 1). And sent as detected, which the
  // server applies only while the saved zone is not one chosen by hand, so a
  // late save from another tab cannot undo a choice made since (round 1).
  const timeZoneUserId = user?.id;
  const savedTimeZone = user?.timeZone;
  const timeZoneSource = user?.timeZoneSource;
  useEffect(() => {
    if (!timeZoneUserId || timeZoneSource === undefined || timeZoneSource === 'manual') return;
    const here = canonicalTimeZone(browserTimeZone());
    if (!here || here === canonicalTimeZone(savedTimeZone)) return;
    setDisplayTimeZone(here);
    setUser((current) => (current && current.id === timeZoneUserId ? { ...current, timeZone: here, timeZoneSource: 'auto' } : current));
    void api.patchPreferences({ timeZone: here, timeZoneSource: 'auto', timeZoneDetected: true })
      .then((saved) => {
        // Chosen by hand meanwhile, in another tab: that zone stands. Only
        // while this page still shows the detection this answers, so a slow
        // answer cannot undo a choice made here since (Codex, round 2).
        if (saved.timeZoneSource !== 'manual' || typeof saved.timeZone !== 'string') return;
        const current = userRef.current;
        if (!current || current.id !== timeZoneUserId || current.timeZoneSource !== 'auto' || current.timeZone !== here) return;
        setDisplayTimeZone(saved.timeZone);
        setUser((latest) => (latest && latest.id === timeZoneUserId && latest.timeZoneSource === 'auto' && latest.timeZone === here
          ? { ...latest, timeZone: saved.timeZone as string, timeZoneSource: 'manual' } : latest));
      })
      .catch(() => { /* saved on the next load */ });
  }, [timeZoneUserId, savedTimeZone, timeZoneSource]);

  // Load /me ONCE on mount. Previously the effect depended on
  // `currentCompanyId` which made it re-run any time the user switched
  // company, re-fetching /me for no reason and occasionally racing the
  // state write that selects a default company. Two effects now — one for
  // the initial fetch (empty deps), one for the default-company selection.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { user, companies, extensionVersion, features } = await api.me();
        if (cancelled || !commitUser(user)) return;
        setCompanies(companies);
        setExtensionVersion(extensionVersion ?? EMPTY_EXTENSION_VERSION);
        setFeatures(normalizeFeatures(features));
      } catch {
        /* unauthenticated — stay on sign-in */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [commitUser]);

  // Once /me has answered, the selected company is one of this person's: the
  // one already selected, else their first, else none. Runs whenever companies
  // change; idempotent because each branch settles. A selection that is not
  // one of theirs, left by another account or one they were removed from, is
  // replaced rather than kept: it left the page with no company at all, or,
  // with none left, sent onboarding to build in a workspace they had lost
  // (review of 0.2.0, 3 Oct).
  useEffect(() => {
    if (loading) return;
    if (currentCompanyId && companies.some((company) => company.id === currentCompanyId)) return;
    const next = companies[0]?.id ?? null;
    if (next === currentCompanyId) return;
    setCurrentCompanyIdState(next);
    try {
      if (next) localStorage.setItem(CURRENT_CO_KEY, next);
      else localStorage.removeItem(CURRENT_CO_KEY);
    } catch { /* noop */ }
  }, [companies, currentCompanyId, loading]);

  /**
   * A 401: one of this page's requests was refused a session.
   *
   * What it means depends on who the browser's cookie holds now, so that is
   * asked first (`whoIsSignedIn`), before anything is cleared (review of
   * 0.2.0, 3 Oct). For a page that held a session:
   * - Nobody: its session ended. It keeps showing what it showed until the
   *   recorder lets go, clears what the session left in this browser, and is
   *   loaded afresh at the same address, where its route decides what a
   *   signed-out visitor sees. It shows no sign-in form first, since one filled
   *   in meanwhile would have let the next person into this page.
   * - Another account, signed in from another tab: the page is loaded afresh as
   *   them and clears nothing, because what the browser holds is theirs now,
   *   their recording included.
   * - Its own: the 401 ended nothing, and the page carries on.
   * - No answer: the page is loaded afresh, where /me asks again, and clears
   *   nothing on a guess.
   * A page that held no session, a signed-out visitor's, has none to end, and
   * its route already decides (RequireAuth sends a protected one to sign-in),
   * so a public page such as an invite or a report form stays. It clears what
   * an earlier session left only when nobody is signed in: a 401 can also be a
   * passing one, while somebody is signed in, and recording, in another tab.
   * Clearing takes the `vd-` keys and the recorder's blobs in IndexedDB, so a
   * shared-machine user cannot recover the previous user's drafts, screen or
   * voice.
   *
   * One check at a time. A 401 that arrives while one is out is not dropped:
   * the session may have ended after that answer was given, so it is asked
   * again once the check is done. A page told to leave that is still here,
   * because somebody chose to stay at the "Leave site?" prompt, checks its
   * next 401 again.
   *
   * Clearing that fails part way, a recorder in another tab that will not let
   * go, is not retried here: the page loads afresh signed out, its own /me is
   * answered 401, and it clears then.
   */
  useEffect(() => {
    const clear = async (endedUserId: string | undefined) => {
      invalidateAuthSession();
      purgeUserLocalState();
      let releaseRecorderLease: (() => void) | undefined;
      try {
        releaseRecorderLease = await prepareRecorderForSignOut(endedUserId);
        await purgeRecorderDataForSignOut();
      } catch {
        // Cleared by the next page, as above.
      } finally {
        releaseRecorderLease?.();
      }
      if (endedUserId) leavePage();
    };
    let checking = false;
    let askAgain = false;
    /** Whether to stop asking: the page held a session and has been told to leave. */
    const check = async (): Promise<boolean> => {
      const heldId = userRef.current?.id;
      let current: string | null;
      try {
        current = await whoIsSignedIn();
      } catch {
        if (heldId) leavePage();
        return Boolean(heldId);
      }
      if (heldId && current === heldId) return false;
      if (heldId && current) leavePage();
      else if (current === null) await clear(heldId);
      return Boolean(heldId);
    };
    const onUnauthorized = () => {
      if (checking) {
        askAgain = true;
        return;
      }
      checking = true;
      void (async () => {
        try {
          do {
            askAgain = false;
            if (await check()) return;
          } while (askAgain);
        } finally {
          checking = false;
        }
      })();
    };
    window.addEventListener('vd:unauthorized', onUnauthorized);
    return () => window.removeEventListener('vd:unauthorized', onUnauthorized);
  }, []);

  // Stable callbacks — without these, the memoized context value below
  // would churn every render and defeat the point of the memo.
  const setCurrentCompany = useCallback((companyId: string) => {
    setCurrentCompanyIdState(companyId);
    try { localStorage.setItem(CURRENT_CO_KEY, companyId); } catch { /* noop */ }
    // The extension follows the switch (t-LnUUVu): captures and pins with no
    // project of their own go to the workspace somebody is looking at.
    tellExtensionWorkspaceChanged(window, companyId);
  }, []);

  // `signIn` / `signOut` / `refreshUser` close over setters that React
  // guarantees are stable, so we can mark them `useCallback` with an empty
  // dep list. Capture the current-company setter via ref to keep that
  // guarantee even though the setter identity is technically stable too.
  const setCompanyRef = useRef(setCurrentCompany);
  setCompanyRef.current = setCurrentCompany;

  const signIn = useCallback(async (email: string, password: string) => {
    const { user } = await api.signIn(email, password);
    if (!commitUser(user)) return;
    const me = await api.me();
    // Prefer /me's user shape over the signin response: /me includes
    // fields the signin handler doesn't bother to populate (e.g.,
    // isStaff for the admin-panel TopBar link). Without this, a fresh
    // signin lands in the app with a partial user object and admin
    // staff don't see their menu entry until a hard refresh.
    if (!commitUser(me.user)) return;
    setCompanies(me.companies);
    setExtensionVersion(me.extensionVersion ?? EMPTY_EXTENSION_VERSION);
    setFeatures(normalizeFeatures(me.features));
    if (me.companies[0]) setCompanyRef.current(me.companies[0].id);
  }, [commitUser]);

  const signOut = useCallback(async () => {
    const signingOutUser = userRef.current;
    // Another account, signed in from another tab, holds what the browser has
    // now: this page ends nothing of theirs and clears nothing of theirs, and
    // is loaded afresh as them (review of 0.2.0, 3 Oct). Asked before the
    // recorder is told anything, since preparing stops every tab's recorder.
    const current = await whoIsSignedIn().catch(() => signingOutUser?.id ?? null);
    if (signingOutUser && current && current !== signingOutUser.id) {
      leavePage('/', { confirmed: true });
      return;
    }
    /**
     * Preparing can refuse, and refusing must leave the recorder usable.
     *
     * It waits for the other tabs to stop writing and fails rather than
     * deleting recordings alongside a writer, which is the right answer to
     * that question. But the local preparation has already stopped this tab's
     * recorder and let go of its uploads by then, so without this the person
     * is still signed in with a recorder that quietly does nothing until they
     * reload. The cancel is the same one a failed sign-out request sends.
     */
    let releaseRecorderLease: () => void;
    try {
      releaseRecorderLease = await prepareRecorderForSignOut(signingOutUser?.id);
    } catch (error) {
      cancelRecorderSignOut();
      throw error;
    }
    setAuthSessionUser(null);
    const signOutEpoch = authSessionEpoch();
    try {
      await api.signOut();
      await purgeRecorderDataForSignOut().catch(() => {});
    } catch (error) {
      // A 401 while sign-out is in flight force-rotates the null epoch. Do
      // not resurrect the old user after that newer invalidation signal.
      if (authSessionEpoch() === signOutEpoch) {
        setAuthSessionUser(signingOutUser?.id ?? null);
        cancelRecorderSignOut();
      }
      // The same as above, found by the server: somebody signed in meanwhile.
      if ((error as ApiError).code === 'account_changed') {
        leavePage('/', { confirmed: true });
        return;
      }
      throw error;
    } finally {
      releaseRecorderLease();
    }
    // Purge ALL vd-* local state so shared-machine users can't read the
    // previous user's drawings, uploaded screenshots, or project MRU.
    purgeUserLocalState();
    // Then leave the page, which takes the react-query cache and whatever this
    // session still had going with it. The person agreed to leave before
    // sign-out started (TopBar asks), so they are not asked again now.
    leavePage('/signin', { confirmed: true });
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await api.me();
    if (!commitUser(me.user)) return;
    setCompanies(me.companies);
    setExtensionVersion(me.extensionVersion ?? EMPTY_EXTENSION_VERSION);
    setFeatures(normalizeFeatures(me.features));
  }, [commitUser]);

  // Listen for project-access changes affecting the signed-in user.
  // When an owner restricts (or unrestricts) this user, the backend
  // broadcasts `member.access_changed`; we refetch /me to pick up the
  // new `accessMode` and invalidate project/ticket/inbox/search
  // queries so the next interaction shows the new visibility.
  const qc = queryClient;
  // Deps include `user?.id` (not the user object) so a `setUser` that
  // produces a new object reference with the same id (post-/me refetch,
  // avatar tweak, etc.) doesn't tear down + recreate the live-bus
  // subscription. Each tear-down drops events queued between unsubscribe
  // and re-subscribe; flapping is silent data loss.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    return api.subscribe((e: LiveEvent) => {
      if (e.type !== 'member.access_changed') return;
      if (e.userId !== userId) return;
      void refreshUser();
      qc.invalidateQueries({ queryKey: ['projects', e.companyId] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      // The sidebar's counts and the bell read the same scope (Codex review).
      qc.invalidateQueries({ queryKey: ['inbox-counts'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['search'] });
    });
  }, [userId, qc, refreshUser]);

  /**
   * Tell the extension when a ticket stops being a pin (TOADO-405).
   *
   * A separate subscription rather than a branch in the one above, because it
   * shares nothing with it: no user id, no query invalidation, and nothing to
   * refetch on this side. Folding it in would mean one handler where a change
   * to either concern can break the other.
   *
   * Mounted here because this provider is the one component guaranteed to be
   * alive on every signed-in route, so the relay does not depend on which page
   * somebody happens to be looking at when they archive something.
   */
  useEffect(() => {
    if (!userId) return;
    return api.subscribe((e: LiveEvent) => { relayTicketChangeToExtension(e); });
  }, [userId]);

  // Memoize the context value so consumers only re-render when a field
  // they actually depend on changes. Previously every AuthProvider render
  // minted a fresh object and cascaded re-renders through every consumer
  // of the auth context — a real hot path in a keyboard-shortcut-driven
  // app with many small subscribers.
  const value = useMemo<AuthState>(() => ({
    user, companies, loading, currentCompanyId, extensionVersion, features,
    setCurrentCompany, signIn, signOut, refreshUser,
  }), [user, companies, loading, currentCompanyId, extensionVersion, features, setCurrentCompany, signIn, signOut, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function useCurrentCompany() {
  const { companies, currentCompanyId } = useAuth();
  return companies.find((c) => c.id === currentCompanyId) ?? null;
}
